  /**
   * Lookbook Mobile Navigation
   * 
   * Generates mobile tab navigation for lookbook sections.
   * Scans the page for [data-lookbook-section] elements and creates a
   * navigation bar with tabs, prev/next buttons, and full accessibility support.
   */

  document.addEventListener('DOMContentLoaded', () => {
    (() => {

      // Find all lookbook sections on the page
      const sections = document.querySelectorAll('[data-lookbook-section]');

      // Exit if < 2 sections (no nav needed for single section)
      if (sections.length < 2) {
        return;
      }

      // SVG icons for prev/next buttons:
      // Cannot use Liquid's {% render 'icon' %} at runtime as Liquid
      // runs server-side only. This approach is standard for JS-generated components
      // and keeps the navigation self-contained. Uses currentColor to inherit theme styling.
      const prevIcon = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 4L4 12L12 20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

      const nextIcon = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 4L16 12L8 20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

      // Create navigation container
      const nav = document.createElement('nav');
      nav.className = 'lookbook-mobile-nav';
      nav.setAttribute('role', 'tablist');
      nav.setAttribute('aria-label', 'Lookbook navigation');

      // Create prev button
      const prevButton = document.createElement('button');
      prevButton.className = 'lookbook-nav-btn lookbook-nav-prev';
      prevButton.setAttribute('aria-label', 'Previous look');
      prevButton.innerHTML = prevIcon;

      // Create tabs container
      const tabsContainer = document.createElement('div');
      tabsContainer.className = 'lookbook-nav-tabs';

      // Create next button
      const nextButton = document.createElement('button');
      nextButton.className = 'lookbook-nav-btn lookbook-nav-next';
      nextButton.setAttribute('aria-label', 'Next look');
      nextButton.innerHTML = nextIcon;

      // Generate tab buttons for each section
      /** @type {HTMLButtonElement[]} */
      const tabs = [];
      sections.forEach((section, index) => {
        const lookName = /** @type {HTMLElement} */ (section).dataset.lookName || `Look ${index + 1}`;
        const sectionId = section.id;

        const button = document.createElement('button');
        button.className = 'lookbook-nav-tab';
        button.textContent = lookName;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', sectionId);
        button.setAttribute('id', `tab-${sectionId}`);
        button.dataset.targetSection = sectionId;
        button.tabIndex = index === 0 ? 0 : -1;

        if (index === 0) {
          button.classList.add('active');
          button.setAttribute('aria-selected', 'true');
        } else {
          button.setAttribute('aria-selected', 'false');
        }

        tabs.push(button);
        tabsContainer.appendChild(button);
      });

      // Assemble navigation
      nav.appendChild(prevButton);
      nav.appendChild(tabsContainer);
      nav.appendChild(nextButton);

      // Insert nav before first section
      const firstSection = sections[0];
      if (firstSection && firstSection.parentNode) {
        firstSection.parentNode.insertBefore(nav, firstSection);
      }

      // Set initial active state
      if (sections[0]) {
        sections[0].classList.add('active-look');
      }

      // Function to activate a specific section
      /**
       * @param {number} targetIndex
       */
      const activateSection = (targetIndex) => {
        if (targetIndex < 0 || targetIndex >= sections.length) {
          return;
        }

        // Update sections
        sections.forEach((section, index) => {
          if (index === targetIndex) {
            section.classList.add('active-look');
            section.removeAttribute('aria-hidden');
          } else {
            section.classList.remove('active-look');
            section.setAttribute('aria-hidden', 'true');
          }
        });

        // Update tabs
        tabs.forEach((tab, index) => {
          if (index === targetIndex) {
            tab.classList.add('active');
            tab.setAttribute('aria-selected', 'true');
            tab.tabIndex = 0;
            
            // Scroll tab into view (smooth, inline: start)
            tab.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'start'
            });
          } else {
            tab.classList.remove('active');
            tab.setAttribute('aria-selected', 'false');
            tab.tabIndex = -1;
          }
        });
      };

      // Get current active index
      const getCurrentIndex = () => {
        return tabs.findIndex(tab => tab.classList.contains('active'));
      };

      // Tab click handlers
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', /** @param {Event} event */ (event) => {
          event.preventDefault();
          activateSection(index);
        });
      });

      // Prev button click handler
      prevButton.addEventListener('click', () => {
        const currentIndex = getCurrentIndex();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : sections.length - 1;
        activateSection(prevIndex);
      });

      // Next button click handler
      nextButton.addEventListener('click', () => {
        const currentIndex = getCurrentIndex();
        const nextIndex = currentIndex < sections.length - 1 ? currentIndex + 1 : 0;
        activateSection(nextIndex);
      });

      // Keyboard navigation for tabs
      tabsContainer.addEventListener('keydown', (event) => {
        const currentIndex = getCurrentIndex();

        switch (event.key) {
          case 'ArrowLeft': {
            event.preventDefault();
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : sections.length - 1;
            activateSection(prevIndex);
            if (tabs[prevIndex]) {
              tabs[prevIndex].focus();
            }
            break;
          }

          case 'ArrowRight': {
            event.preventDefault();
            const nextIndex = currentIndex < sections.length - 1 ? currentIndex + 1 : 0;
            activateSection(nextIndex);
            if (tabs[nextIndex]) {
              tabs[nextIndex].focus();
            }
            break;
          }

          case 'Home': {
            event.preventDefault();
            activateSection(0);
            if (tabs[0]) {
              tabs[0].focus();
            }
            break;
          }

          case 'End': {
            event.preventDefault();
            activateSection(sections.length - 1);
            const lastTab = tabs[sections.length - 1];
            if (lastTab) {
              lastTab.focus();
            }
            break;
          }

          case 'Enter':
          case ' ': {
            event.preventDefault();
            const focusedIndex = tabs.findIndex(tab => tab === document.activeElement);
            if (focusedIndex !== -1) {
              activateSection(focusedIndex);
            }
            break;
          }
        }
      });
    })();
  });

